import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  signUp as signUpUrl,
  verifyUser as verifyUserUrl,
  resendCode as resendCodeUrl,
  signIn as signInUrl,
} from "constants/apiUrls";

// Sign-Up Request Payload
export interface SignUpRequestPayload {
  name: string;
  email: string;
  password: string;
  phoneNumber: string;
  phoneCountryExtension: string;
  phoneCountryName: string;
  phoneCountryCode: string;
  persona: string;
  state?: string;
  stateShortCode?: string;
  city?: string;
  country?: string;
  countryShortCode?: string;
  deviceToken?: string;
  logo?: File | null;
  referralCode?: string; // Optional referral code
}

// Sign-Up Response
export interface SignUpResponse {
  uuid?: string;
  email?: string;
  phoneNumber?: string;
  name?: string;
  message?: string;
  access?: string;
  refresh?: string;
  lastOtpDetails?: {
    persona: string;
    sentTo: string;
  };
  [key: string]: any; // Allow additional fields
}

const signUpApi = createApi({
  reducerPath: "signUpApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "",
    prepareHeaders: (headers, { endpoint }) => {
      // Always add bludot-app-name header
      headers.set("bludot-app-name", "business-rewards");

      // For verify endpoint, headers are already set in the query function
      // prepareHeaders merges them automatically

      return headers;
    },
  }),
  endpoints: (builder) => ({
    signUp: builder.mutation<
      SignUpResponse,
      { payload: SignUpRequestPayload; verifyWith: string }
    >({
      query: ({ payload, verifyWith }) => {
        // Create FormData for multipart/form-data
        const formData = new FormData();

        // Add all fields to FormData
        formData.append("logo", ""); // Empty logo field
        formData.append("name", payload.name);
        formData.append("email", payload.email);
        formData.append("password", payload.password);
        formData.append("phoneCountryExtension", payload.phoneCountryExtension);
        formData.append("phoneCountryName", payload.phoneCountryName);
        formData.append("phoneCountryCode", payload.phoneCountryCode);
        formData.append("phoneNumber", payload.phoneNumber);
        formData.append("persona", payload.persona);
        formData.append("verifyWith", verifyWith);
        formData.append("state", payload.state || "");
        formData.append("stateShortCode", payload.stateShortCode || "");
        formData.append("city", payload.city || "");
        formData.append("country", payload.country || "");
        formData.append("countryShortCode", payload.countryShortCode || "");
        formData.append("deviceToken", payload.deviceToken || "");
        
        // Add optional referral code if provided
        if (payload.referralCode) {
          formData.append("referralCode", payload.referralCode);
        }

        return {
          url: signUpUrl(verifyWith),
          method: "POST",
          body: formData,
        };
      },
    }),
    verifyUser: builder.mutation<
      SignUpResponse,
      {
        otp_code: string;
        sendTo: string;
        email?: string;
        phoneNumber?: string;
        giftInviteToken?: string | null;
      }
    >({
      query: ({ otp_code, sendTo, giftInviteToken = null }) => {
        // Only send otp_code and giftInviteToken
        // User identification comes from Authorization header
        const requestBody: any = {
          otp_code,
          giftInviteToken,
        };

        // Get token for authorization (user is already logged in)
        const token =
          localStorage.getItem("signupToken") || localStorage.getItem("token");
        const headers: any = {
          "Content-Type": "application/json",
        };

        // Add authorization header if token exists
        if (token) {
          try {
            headers.Authorization = `Bearer ${JSON.parse(token)}`;
          } catch (e) {
            // Failed to parse token
          }
        }

        return {
          url: verifyUserUrl(sendTo),
          method: "POST",
          body: requestBody,
          headers: headers,
        };
      },
    }),
    resendCode: builder.mutation<
      { message: string },
      { sendTo: string; email?: string; phoneNumber?: string; persona?: string }
    >({
      query: ({ sendTo, email, phoneNumber, persona = "consumer" }) => {
        const body: any = { persona };

        // Add email or phoneNumber
        if (email) {
          body.email = email;
        }
        if (phoneNumber) {
          body.phoneNumber = phoneNumber;
        }

        const url = resendCodeUrl(sendTo);

        // Get token for authorization (user is already logged in during verification)
        const token =
          localStorage.getItem("signupToken") || localStorage.getItem("token");
        const headers: any = {
          "Content-Type": "application/json",
        };

        // Add authorization header if token exists
        if (token) {
          try {
            headers.Authorization = `Bearer ${JSON.parse(token)}`;
          } catch (e) {
            // Failed to parse token
          }
        }

        return {
          url: url,
          method: "POST",
          body: JSON.stringify(body),
          headers: headers,
        };
      },
    }),
    login: builder.mutation<
      SignUpResponse,
      { email: string; password: string }
    >({
      query: ({ email, password }) => {
        return {
          url: signInUrl,
          method: "POST",
          body: JSON.stringify({ email, password }),
          headers: {
            "Content-Type": "application/json",
          },
        };
      },
    }),
  }),
});

export const {
  useSignUpMutation,
  useVerifyUserMutation,
  useResendCodeMutation,
  useLoginMutation,
} = signUpApi;
export default signUpApi;
